import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminListPrayers,
  adminUpdatePrayerStatus,
  adminListSettings,
  adminUpdateSetting,
  adminListPlans,
  adminCreatePlan,
  adminUpdatePlan,
  adminDeletePlan,
  adminPurgeCache,
} from "../lib/api";
import { useAuth } from "../lib/authContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Trash2,
  Save,
  LogOut,
  Pencil,
  X,
  RefreshCw,
  ExternalLink,
  ShieldAlert,
  LayoutDashboard,
  Library,
  MessageSquare,
  Sparkles,
  Settings as SettingsIcon,
  Megaphone,
  PhoneCall,
  UserCheck,
  Inbox,
  HandHeart,
  ChevronRight,
  Youtube,
  Film,
  HardDrive,
  Menu,
  Search,
  ChevronDown,
} from "lucide-react";
import AdminInboxTab from "./AdminInboxTab";
import AdminUsersSettings from "./AdminUsersSettings";
import AdminPendingPrayers from "./AdminPendingPrayers";
import WhatsAppPreview from "./WhatsAppPreview";
import CrisisNotifySettings from "./CrisisNotifySettings";
import AdminContacts from "./AdminContacts";
import AdminPastoralTeam from "./AdminPastoralTeam";
import FanoutSettings from "./FanoutSettings";
import WhatsAppWebhookConfig from "./WhatsAppWebhookConfig";
import AdminSermonsModule from "./AdminSermonsModule";
import FeaturedVideoAdmin from "../components/admin/FeaturedVideoAdmin";
import PalavraMinutoAdmin from "../components/admin/PalavraMinutoAdmin";
import StorageOrphansAdmin from "../components/admin/StorageOrphansAdmin";
import ConnectionsAuditTab from "./admin/ConnectionsAuditTab";
import { ContentSourceStatus } from "../components/admin/ContentSourceStatus";
import { inboxApi } from "../lib/inboxApi";

// ---------------------------------------------------------------------------
// Form fields (reused locally)
// ---------------------------------------------------------------------------
const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-navy/60">
      {label}
    </span>
    <Input
      className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
      {...props}
    />
  </label>
);

const TextField = ({ label, rows = 4, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-navy/60">
      {label}
    </span>
    <Textarea
      rows={rows}
      className="mt-2 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
      {...props}
    />
  </label>
);

// ---------------------------------------------------------------------------
// Login / forbidden states
// ---------------------------------------------------------------------------
function LoginView() {
  return (
    <div
      className="min-h-[70vh] flex items-center justify-center section-padding"
      data-testid="admin-login"
    >
      <div className="w-full max-w-md bg-white rounded-2xl border border-border p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center mx-auto">
          <ShieldAlert className="w-5 h-5 text-gold" />
        </div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mt-5">
          Painel administrativo
        </p>
        <h1 className="font-display text-3xl text-navy mt-2">
          Acesso restrito
        </h1>
        <p className="text-sm text-navy/70 mt-3 leading-relaxed">
          Esta área é exclusiva do administrador. Entre com sua conta para
          continuar.
        </p>
        <Link
          to="/entrar?next=/admin"
          data-testid="admin-login-cta"
          className="inline-flex items-center justify-center gap-2 mt-6 bg-navy text-white hover:bg-gold hover:text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
        >
          Entrar como administrador
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------
function DashboardSection({ counts, onJump }) {
  const cards = [
    {
      key: "inbox",
      title: "Mensagens",
      icon: Inbox,
      value: (counts?.novo || 0) + (counts?.escalado || 0),
      hint: "novas ou escaladas",
      target: "inbox",
    },
    {
      key: "prayers",
      title: "Orações",
      icon: HandHeart,
      value: counts?.prayers || ", ",
      hint: "pedidos abertos",
      target: "prayers",
    },
    {
      key: "contacts",
      title: "Contatos",
      icon: PhoneCall,
      value: counts?.contacts || ", ",
      hint: "na base WhatsApp",
      target: "contacts",
    },
    {
      key: "sermons",
      title: "IA Sermões",
      icon: Sparkles,
      value: counts?.sermons || ", ",
      hint: "salvos",
      target: "sermons",
    },
  ];
  return (
    <div className="space-y-6" data-testid="admin-dashboard-section">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Visão geral
        </p>
        <h2 className="font-display text-3xl text-navy mt-1">Dashboard</h2>
        <p className="text-sm text-navy/65 mt-2 max-w-xl leading-relaxed">
          Acompanhe o ministério em um relance. Toque em um cartão para abrir a
          área correspondente.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => onJump(c.target)}
              data-testid={`dashboard-card-${c.key}`}
              className="text-left bg-white border border-border rounded-2xl p-5 hover:border-gold/70 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center">
                  <Icon className="w-4 h-4 text-gold" />
                </div>
                <ChevronRight className="w-4 h-4 text-navy/30 group-hover:text-gold transition-colors" />
              </div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55 mt-4">
                {c.title}
              </p>
              <p className="font-display text-3xl text-navy mt-1">{c.value}</p>
              <p className="text-xs text-navy/55 mt-1">{c.hint}</p>
            </button>
          );
        })}
      </div>

      <Card className="p-6 border-border bg-[hsl(var(--parchment))]">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-gold" />
          </div>
          <div className="flex-1">
            <p className="font-display text-xl text-navy">
              Novo: módulo IA Sermões
            </p>
            <p className="text-sm text-navy/70 mt-1 leading-relaxed">
              Gere sermões expositivos completos (11 seções), edite, salve em
              rascunho/final e exporte em PDF, restrito a administradores.
            </p>
            <Button
              size="sm"
              onClick={() => onJump("sermons")}
              data-testid="dashboard-cta-sermons"
              className="mt-3 rounded-full text-xs bg-navy text-white hover:bg-gold hover:text-navy"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Abrir Faça seu Sermão
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ContentGatewayTab() {
  const [loading, setLoading] = useState(false);

  const purge = async () => {
    setLoading(true);
    try {
      const r = await adminPurgeCache();
      toast.success(`Cache limpo (${r.purged} entradas).`);
    } catch {
      toast.error("Erro ao limpar cache.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-content-tab">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Conteúdo
        </p>
        <h2 className="font-display text-3xl text-navy mt-1">
          Devocionais, sermões, vídeos e livros
        </h2>
      </div>
      <Card className="p-6 border-border bg-white">
        <p className="text-sm text-navy/70 leading-relaxed">
          Esses conteúdos são gerenciados diretamente no painel de{" "}
          <span className="font-medium text-navy">cemedeiros.com.br</span> e
          consumidos em tempo real pelo aplicativo. Qualquer alteração lá
          aparece aqui automaticamente (após o cache de 5 minutos).
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href="https://cemedeiros.com.br/admin"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-landing-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-navy text-white px-5 py-2.5 rounded-full hover:bg-gold hover:text-navy transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Abrir painel da landing
          </a>
          <Button
            onClick={purge}
            disabled={loading}
            variant="outline"
            data-testid="admin-cache-purge"
            className="rounded-full uppercase tracking-widest text-xs"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            {loading ? "Limpando..." : "Limpar cache agora"}
          </Button>
        </div>
      </Card>
      <Card className="p-6 border-border bg-[hsl(var(--parchment))]">
        <p className="text-xs uppercase tracking-[0.25em] text-navy/60">
          Gerenciado neste app
        </p>
        <ul className="mt-3 text-sm text-navy/80 space-y-1.5">
          <li>· Planos de leitura</li>
          <li>· Pedidos de oração</li>
          <li>· Configurações (WhatsApp e mensagem padrão)</li>
        </ul>
      </Card>
    </div>
  );
}

function PrayersTab() {
  const [list, setList] = useState([]);
  const reload = () =>
    adminListPrayers()
      .then(setList)
      .catch(() => {});
  useEffect(() => {
    reload();
  }, []);

  const setStatus = async (id, status) => {
    await adminUpdatePrayerStatus(id, status);
    reload();
  };

  return (
    <div data-testid="admin-prayers-tab" className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Pastoral
        </p>
        <h2 className="font-display text-3xl text-navy mt-1">
          Pedidos de oração
        </h2>
      </div>
      <AdminPendingPrayers />
      <div className="space-y-3">
        <h3 className="font-display text-xl text-navy">Fluxo interno</h3>
        {list.length === 0 && (
          <p className="text-navy/60 text-sm">Nenhum pedido recebido.</p>
        )}
        {list.map((p) => (
          <Card
            key={p.id}
            className="p-4 border-border"
            data-testid={`admin-prayer-item-${p.id}`}
          >
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h4 className="font-display text-lg text-navy">{p.name}</h4>
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                      p.status === "new"
                        ? "text-gold border-gold/60"
                        : "text-navy/50 border-navy/20"
                    }`}
                  >
                    {p.status}
                  </span>
                </div>
                <p className="text-xs text-navy/60 mt-1">
                  {p.created_at?.slice(0, 10)} {p.contact && `· ${p.contact}`}
                </p>
                <p className="text-sm text-navy/80 mt-3 whitespace-pre-wrap">
                  {p.request}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setStatus(p.id, p.status === "done" ? "new" : "done")
                  }
                >
                  {p.status === "done" ? "Marcar como novo" : "Marcar orado"}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [list, setList] = useState([]);
  const [drafts, setDrafts] = useState({});
  const reload = () =>
    adminListSettings()
      .then((ls) => {
        setList(ls);
        const d = {};
        ls.forEach((s) => {
          d[s.key] = s.value;
        });
        setDrafts(d);
      })
      .catch(() => {});
  useEffect(() => {
    reload();
  }, []);

  const save = async (key) => {
    await adminUpdateSetting(key, drafts[key] || "");
    toast.success("Configuração salva");
    reload();
  };

  const add = async (key) => {
    if (!key) return;
    await adminUpdateSetting(key, drafts.__new__ || "");
    setDrafts({ ...drafts, __new__: "", __newKey__: "" });
    reload();
  };

  return (
    <div data-testid="admin-settings-tab" className="max-w-3xl space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Sistema
        </p>
        <h2 className="font-display text-3xl text-navy mt-1">Configurações</h2>
      </div>

      <WhatsAppPreview />
      <WhatsAppWebhookConfig />
      <CrisisNotifySettings />
      <FanoutSettings />
      <AdminUsersSettings />

      <Card className="p-5 border-border bg-white">
        <h3 className="font-display text-xl text-navy">Chaves do app</h3>
        <div className="space-y-3 mt-3">
          {list.map((s) => (
            <div key={s.key} className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-widest text-navy/60">
                {s.key}
              </p>
              <div className="flex gap-2">
                <Input
                  value={drafts[s.key] || ""}
                  onChange={(e) =>
                    setDrafts({ ...drafts, [s.key]: e.target.value })
                  }
                  data-testid={`admin-setting-${s.key}`}
                  className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
                />
                <Button
                  onClick={() => save(s.key)}
                  className="rounded-full bg-navy text-white hover:bg-gold hover:text-navy"
                >
                  Salvar
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-border">
          <p className="text-xs uppercase tracking-widest text-navy/60 mb-2">
            Nova chave
          </p>
          <div className="flex flex-col gap-2">
            <Input
              placeholder="chave"
              value={drafts.__newKey__ || ""}
              onChange={(e) =>
                setDrafts({ ...drafts, __newKey__: e.target.value })
              }
            />
            <Input
              placeholder="valor"
              value={drafts.__new__ || ""}
              onChange={(e) =>
                setDrafts({ ...drafts, __new__: e.target.value })
              }
            />
            <Button
              onClick={() => add(drafts.__newKey__)}
              className="rounded-full bg-navy text-white hover:bg-gold hover:text-navy w-fit"
            >
              Criar
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function PlansTab() {
  const emptyDay = () => ({
    day: 1,
    title: "",
    verse_ref: "",
    verse_text: "",
    reflection: "",
    application: "",
    prayer: "",
  });
  const empty = {
    slug: "",
    title: "",
    description: "",
    duration_days: 5,
    order: 0,
    cover_color: "#0B1E3F",
    days: [emptyDay()],
  };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const reload = () =>
    adminListPlans()
      .then(setList)
      .catch(() => {});
  useEffect(() => {
    reload();
  }, []);

  const onChange = (k) => (e) =>
    setForm({
      ...form,
      [k]: ["duration_days", "order"].includes(k)
        ? parseInt(e.target.value || 0, 10)
        : e.target.value,
    });

  const updateDay = (idx, k, v) => {
    const days = [...form.days];
    days[idx] = { ...days[idx], [k]: k === "day" ? parseInt(v || 1, 10) : v };
    setForm({ ...form, days });
  };

  const addDay = () => {
    const nextNum =
      (form.days[form.days.length - 1]?.day || form.days.length) + 1;
    setForm({ ...form, days: [...form.days, { ...emptyDay(), day: nextNum }] });
  };

  const removeDay = (idx) => {
    const days = form.days.filter((_, i) => i !== idx);
    setForm({ ...form, days });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, duration_days: form.days.length };
      if (editing) await adminUpdatePlan(editing, payload);
      else await adminCreatePlan(payload);
      toast.success("Plano salvo");
      setForm(empty);
      setEditing(null);
      reload();
    } catch {
      toast.error("Erro ao salvar plano");
    }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({
      slug: p.slug,
      title: p.title,
      description: p.description,
      duration_days: p.duration_days,
      order: p.order || 0,
      cover_color: p.cover_color || "#0B1E3F",
      days: (p.days || []).length ? p.days : [emptyDay()],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este plano?")) return;
    await adminDeletePlan(id);
    toast.success("Plano removido");
    reload();
  };

  return (
    <div data-testid="admin-plans-tab" className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Conteúdo
        </p>
        <h2 className="font-display text-3xl text-navy mt-1">
          Planos de leitura
        </h2>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <form
          onSubmit={submit}
          className="space-y-4 bg-white p-6 rounded-2xl border border-border"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl text-navy">
              {editing ? "Editar" : "Novo"} plano
            </h3>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setForm(empty);
                }}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Field
            label="Slug (único)"
            value={form.slug}
            onChange={onChange("slug")}
            required
          />
          <Field
            label="Título"
            value={form.title}
            onChange={onChange("title")}
            required
          />
          <TextField
            label="Descrição"
            value={form.description}
            onChange={onChange("description")}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Ordem"
              type="number"
              value={form.order}
              onChange={onChange("order")}
            />
            <Field
              label="Cor de capa (hex)"
              value={form.cover_color}
              onChange={onChange("cover_color")}
            />
          </div>

          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-navy/60">
                Dias do plano ({form.days.length})
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addDay}
                className="rounded-full"
              >
                + Adicionar dia
              </Button>
            </div>
            <div className="space-y-4">
              {form.days.map((d, idx) => (
                <div
                  key={idx}
                  className="border border-border rounded-xl p-4 bg-[hsl(var(--parchment))] space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-navy">
                      Dia {d.day}
                    </span>
                    {form.days.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDay(idx)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </button>
                    )}
                  </div>
                  <Input
                    placeholder="Título do dia"
                    value={d.title}
                    onChange={(e) => updateDay(idx, "title", e.target.value)}
                    required
                  />
                  <Input
                    placeholder="Referência (ex: João 3:16)"
                    value={d.verse_ref}
                    onChange={(e) =>
                      updateDay(idx, "verse_ref", e.target.value)
                    }
                    required
                  />
                  <Textarea
                    placeholder="Texto do versículo"
                    rows={2}
                    value={d.verse_text}
                    onChange={(e) =>
                      updateDay(idx, "verse_text", e.target.value)
                    }
                  />
                  <Textarea
                    placeholder="Reflexão"
                    rows={3}
                    value={d.reflection}
                    onChange={(e) =>
                      updateDay(idx, "reflection", e.target.value)
                    }
                    required
                  />
                  <Textarea
                    placeholder="Aplicação prática"
                    rows={2}
                    value={d.application}
                    onChange={(e) =>
                      updateDay(idx, "application", e.target.value)
                    }
                    required
                  />
                  <Textarea
                    placeholder="Oração"
                    rows={2}
                    value={d.prayer}
                    onChange={(e) =>
                      updateDay(idx, "prayer", e.target.value)
                    }
                    required
                  />
                </div>
              ))}
            </div>
          </div>

          <Button
            type="submit"
            className="rounded-full bg-navy text-white hover:bg-gold hover:text-navy uppercase tracking-widest text-xs"
          >
            <Save className="w-4 h-4 mr-2" /> Salvar plano
          </Button>
        </form>

        <div className="space-y-3">
          {list.map((p) => (
            <Card
              key={p.id}
              className="p-4 border-border"
              data-testid={`admin-plan-item-${p.slug}`}
            >
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="text-xs text-gold">
                    {p.duration_days} dias · {p.slug}
                  </p>
                  <h4 className="font-display text-lg text-navy mt-1">
                    {p.title}
                  </h4>
                  <p className="text-xs text-navy/60 line-clamp-2 mt-1">
                    {p.description}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => edit(p)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(p.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Navigation model (sidebar modules grouped semantically)
// ---------------------------------------------------------------------------
const NAV = [
  {
    group: "Painel",
    items: [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        roles: ["admin", "pastor"],
      },
    ],
  },
  {
    group: "Pastoral",
    items: [
      {
        key: "inbox",
        label: "Mensagens",
        icon: Inbox,
        badge: "inbox",
        roles: ["admin", "pastor", "lider"],
      },
      {
        key: "prayers",
        label: "Orações",
        icon: HandHeart,
        roles: ["admin", "pastor", "lider"],
      },
    ],
  },
  {
    group: "Audiência",
    items: [
      {
        key: "contacts",
        label: "Contatos",
        icon: PhoneCall,
        roles: ["admin", "pastor"],
      },
      {
        key: "team",
        label: "Equipe pastoral",
        icon: UserCheck,
        roles: ["admin"],
      },
    ],
  },
  {
    group: "Conteúdo",
    items: [
      {
        key: "content",
        label: "Gateway de conteúdo",
        icon: Library,
        roles: ["admin", "pastor"],
      },
      {
        key: "plans",
        label: "Planos de leitura",
        icon: MessageSquare,
        roles: ["admin"],
      },
      {
        key: "featured-video",
        label: "Vídeo em Destaque",
        icon: Youtube,
        roles: ["admin"],
      },
      {
        key: "palavra-minuto",
        label: "Palavra em 1 Minuto",
        icon: Film,
        roles: ["admin"],
      },
      {
        key: "connections-audit",
        label: "Auditoria de Conexões",
        icon: ShieldAlert,
        roles: ["admin"],
      },
    ],
  },
  {
    group: "IA Ministerial",
    items: [
      {
        key: "sermons",
        label: "Faça seu Sermão",
        icon: Sparkles,
        roles: ["admin"],
        highlight: true,
      },
    ],
  },
  {
    group: "Sistema",
    items: [
      {
        key: "test-webhook",
        label: "Testar webhook IA",
        icon: Megaphone,
        roles: ["admin"],
        externalRoute: "/admin/test-webhook",
      },
      {
        key: "storage-orphans",
        label: "Arquivos Órfãos",
        icon: HardDrive,
        roles: ["admin"],
      },
      {
        key: "settings",
        label: "Configurações",
        icon: SettingsIcon,
        roles: ["admin"],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function Admin() {
  const { user, isChecking, isAuthenticated, logout, role, hasAnyRole } =
    useAuth();
  const [active, setActive] = useState("dashboard");
  const [counts, setCounts] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !hasAnyRole("admin", "pastor", "lider")) return;
    let alive = true;
    const fetch = async () => {
      try {
        const c = await inboxApi.counts();
        if (alive) setCounts((prev) => ({ ...prev, ...c }));
      } catch {
        /* ignore */
      }
    };
    fetch();
    const id = window.setInterval(fetch, 30_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [isAuthenticated, hasAnyRole]);

  useEffect(() => {
    if (drawerOpen || accountOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen, accountOpen]);

  if (isChecking) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center section-padding"
        data-testid="admin-checking"
      >
        <p className="text-navy/55 text-sm uppercase tracking-widest">
          Verificando sessão…
        </p>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginView />;

  // Qualquer role acima de "user" pode ver pelo menos algumas áreas.
  // 'premium' e 'user' não têm acesso ao painel admin.
  const canViewPanel = hasAnyRole("admin", "pastor", "lider");
  if (!canViewPanel) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center section-padding"
        data-testid="admin-forbidden"
      >
        <div className="max-w-md text-center bg-white border border-border rounded-2xl p-8">
          <ShieldAlert className="w-6 h-6 text-gold mx-auto" />
          <p className="font-display text-2xl text-navy mt-3">Sem permissão</p>
          <p className="text-navy/65 text-sm mt-2">
            Sua conta ({role || "sem papel"}) não tem acesso ao painel
            administrativo.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  const visibleNav = NAV.map((g) => ({
    ...g,
    items: g.items.filter((it) => it.roles.includes(role)),
  })).filter((g) => g.items.length > 0);

  const urgent = (counts?.novo || 0) + (counts?.escalado || 0);

  // Rótulo da seção ativa (usado no cabeçalho mobile).
  const activeLabel =
    visibleNav.flatMap((g) => g.items).find((it) => it.key === active)?.label
    || "Dashboard";

  const renderActive = () => {
    switch (active) {
      case "dashboard":
        return (
          <>
            <ContentSourceStatus />
            <DashboardSection
              counts={{ ...counts, novo: counts?.novo, escalado: counts?.escalado }}
              onJump={setActive}
            />
          </>
        );
      case "inbox":
        return <AdminInboxTab />;
      case "prayers":
        return <PrayersTab />;
      case "contacts":
        return <AdminContacts />;
      case "team":
        return <AdminPastoralTeam />;
      case "content":
        return <ContentGatewayTab />;
      case "plans":
        return <PlansTab />;
      case "featured-video":
        return <FeaturedVideoAdmin />;
      case "palavra-minuto":
        return <PalavraMinutoAdmin />;
      case "connections-audit":
        return <ConnectionsAuditTab />;
      case "storage-orphans":
        return <StorageOrphansAdmin />;
      case "sermons":
        return <AdminSermonsModule />;
      case "settings":
        return <SettingsTab />;
      default:
        return <DashboardSection counts={counts} onJump={setActive} />;
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto section-padding py-4 lg:py-8"
      data-testid="admin-panel"
    >
      <AdminHeader
        user={user}
        role={role}
        logout={logout}
        sectionLabel={activeLabel}
        onOpenDrawer={() => setDrawerOpen(true)}
        onOpenAccount={() => setAccountOpen(true)}
      />

      <div className="grid lg:grid-cols-[260px_1fr] gap-8 items-start">
        {/* Sidebar — desktop only */}
        <aside
          className="hidden lg:block lg:sticky lg:top-24"
          data-testid="admin-sidebar"
        >
          <nav className="bg-white border border-border rounded-2xl p-3 space-y-4">
            {visibleNav.map((group) => (
              <div key={group.group}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-navy/45 px-3 mb-1.5">
                  {group.group}
                </p>
                <ul className="space-y-0.5">
                  {group.items.map((it) => {
                    const Icon = it.icon;
                    const isActive = active === it.key;
                    const badge = it.badge === "inbox" && urgent > 0 ? urgent : null;
                    // Item externo (ex: "Testar webhook IA") vira <Link>.
                    if (it.externalRoute) {
                      return (
                        <li key={it.key}>
                          <Link
                            to={it.externalRoute}
                            data-testid={`admin-nav-${it.key}`}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-navy/75 hover:bg-[hsl(var(--parchment))] transition-colors"
                          >
                            <Icon className="w-4 h-4 text-navy/55" />
                            <span className="flex-1 text-left">{it.label}</span>
                            <ExternalLink className="w-3 h-3 text-navy/40" />
                          </Link>
                        </li>
                      );
                    }
                    return (
                      <li key={it.key}>
                        <button
                          onClick={() => setActive(it.key)}
                          data-testid={`admin-nav-${it.key}`}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                            isActive
                              ? "bg-navy text-white"
                              : it.highlight
                                ? "text-navy hover:bg-gold/15"
                                : "text-navy/75 hover:bg-[hsl(var(--parchment))]"
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${isActive ? "text-gold" : "text-navy/55"} ${it.highlight && !isActive ? "text-gold" : ""}`} />
                          <span className="flex-1 text-left">{it.label}</span>
                          {badge && (
                            <span
                              data-testid={`admin-nav-badge-${it.key}`}
                              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-medium"
                            >
                              {badge > 99 ? "99+" : badge}
                            </span>
                          )}
                          {it.highlight && !badge && (
                            <span className="text-[9px] uppercase tracking-widest text-gold">
                              Novo
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <main className="min-w-0" data-testid="admin-main">
          {renderActive()}
        </main>
      </div>

      {/* Mobile drawer (P0.4B) */}
      <AdminMobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        visibleNav={visibleNav}
        active={active}
        onSelect={(key) => { setActive(key); setDrawerOpen(false); }}
        urgent={urgent}
      />

      {/* Mobile account sheet */}
      <AdminAccountSheet
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        user={user}
        role={role}
        logout={logout}
      />
    </div>
  );
}

function AdminHeader({ user, role, logout, sectionLabel, onOpenDrawer, onOpenAccount }) {
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  return (
    <>
      {/* Mobile header — compacto */}
      <div className="lg:hidden flex items-center justify-between mb-4 gap-2" data-testid="admin-header-mobile">
        <button
          type="button"
          onClick={onOpenDrawer}
          data-testid="admin-open-drawer"
          aria-label="Abrir menu do painel"
          className="w-11 h-11 rounded-full border border-border bg-white flex items-center justify-center hover:bg-[hsl(var(--parchment))]"
        >
          <Menu className="w-5 h-5 text-navy" />
        </button>
        <div className="flex-1 min-w-0 text-center px-2">
          <p className="text-[9px] uppercase tracking-[0.3em] text-navy/50">Admin</p>
          <p className="font-display text-lg text-navy truncate" data-testid="admin-header-section-label">{sectionLabel}</p>
        </div>
        <button
          type="button"
          onClick={onOpenAccount}
          data-testid="admin-open-account"
          aria-label="Abrir menu da conta"
          className="w-11 h-11 rounded-full bg-navy text-gold flex items-center justify-center font-medium"
        >
          {initial}
        </button>
      </div>

      {/* Desktop header — mantém informações completas */}
      <div className="hidden lg:flex items-center justify-between mb-8 gap-3 flex-wrap" data-testid="admin-header-desktop">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">
            Plataforma Pastoral · C E Medeiros
          </p>
          <h1 className="font-display text-4xl text-navy">Administração</h1>
          <p
            className="text-navy/55 text-sm mt-1 flex items-center gap-2 flex-wrap"
            data-testid="admin-user-line"
          >
            Logado como{" "}
            <span className="font-medium text-navy">{user.name}</span> (
            {user.email})
            <span
              data-testid="admin-role-badge"
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-gold/15 text-[10px] uppercase tracking-widest text-navy"
            >
              {role || "user"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            data-testid="admin-back-to-app"
            className="text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold"
          >
            ← Voltar ao aplicativo
          </Link>
          <Button
            variant="outline"
            onClick={logout}
            data-testid="admin-logout"
            className="rounded-full"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// P0.4B — Mobile drawer com grupos em accordion + busca + fecha ao selecionar.
// -----------------------------------------------------------------------------
function AdminMobileDrawer({ open, onClose, visibleNav, active, onSelect, urgent }) {
  const [query, setQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(() =>
    Object.fromEntries(visibleNav.map((g) => [g.group, true]))
  );

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const q = norm(query.trim());
  const filtered = visibleNav.map((g) => ({
    ...g,
    items: q
      ? g.items.filter((it) => norm(it.label).includes(q))
      : g.items,
  })).filter((g) => g.items.length > 0);

  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-[60]" data-testid="admin-drawer" role="dialog" aria-modal="true">
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar menu"
        data-testid="admin-drawer-scrim"
        className="absolute inset-0 bg-navy/60"
      />
      <aside className="absolute top-0 left-0 h-full w-[86%] max-w-[340px] bg-white shadow-xl flex flex-col">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <p className="text-[11px] uppercase tracking-[0.3em] text-navy/55 flex-1">Painel</p>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-drawer-close"
            aria-label="Fechar"
            className="w-9 h-9 rounded-full hover:bg-[hsl(var(--parchment))] flex items-center justify-center"
          >
            <X className="w-4 h-4 text-navy/70" />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-border">
          <label className="relative block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar funcionalidade…"
              data-testid="admin-drawer-search"
              className="w-full pl-9 pr-3 h-11 rounded-full bg-[hsl(var(--parchment))] border-transparent focus:bg-white focus:border-gold/40 text-sm outline-none"
            />
          </label>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1" data-testid="admin-drawer-nav">
          {filtered.length === 0 && (
            <p className="text-sm text-navy/60 px-3 py-6 text-center">
              Nada encontrado para &quot;{query}&quot;.
            </p>
          )}
          {filtered.map((group) => {
            const isOpen = q ? true : expandedGroups[group.group] !== false;
            return (
              <div key={group.group} className="rounded-xl">
                <button
                  type="button"
                  onClick={() => setExpandedGroups({ ...expandedGroups, [group.group]: !isOpen })}
                  data-testid={`admin-drawer-group-${group.group.toLowerCase()}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.25em] text-navy/60 hover:text-navy"
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  <span className="flex-1 text-left">{group.group}</span>
                </button>
                {isOpen && (
                  <ul className="pb-2">
                    {group.items.map((it) => {
                      const Icon = it.icon;
                      const isActive = active === it.key;
                      const badge = it.badge === "inbox" && urgent > 0 ? urgent : null;
                      if (it.externalRoute) {
                        return (
                          <li key={it.key}>
                            <Link
                              to={it.externalRoute}
                              onClick={onClose}
                              data-testid={`admin-drawer-item-${it.key}`}
                              className="w-full flex items-center gap-3 px-3 py-3 mx-1 rounded-lg text-sm text-navy/80 hover:bg-[hsl(var(--parchment))]"
                            >
                              <Icon className="w-4 h-4 text-navy/55" />
                              <span className="flex-1 text-left">{it.label}</span>
                              <ExternalLink className="w-3 h-3 text-navy/40" />
                            </Link>
                          </li>
                        );
                      }
                      return (
                        <li key={it.key}>
                          <button
                            type="button"
                            onClick={() => onSelect(it.key)}
                            data-testid={`admin-drawer-item-${it.key}`}
                            className={`w-full flex items-center gap-3 px-3 py-3 mx-1 rounded-lg text-sm transition-colors ${
                              isActive
                                ? "bg-navy text-white"
                                : it.highlight
                                  ? "text-navy hover:bg-gold/15"
                                  : "text-navy/80 hover:bg-[hsl(var(--parchment))]"
                            }`}
                          >
                            <Icon className={`w-4 h-4 ${isActive ? "text-gold" : it.highlight ? "text-gold" : "text-navy/55"}`} />
                            <span className="flex-1 text-left">{it.label}</span>
                            {badge && (
                              <span
                                data-testid={`admin-drawer-badge-${it.key}`}
                                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-medium"
                              >
                                {badge > 99 ? "99+" : badge}
                              </span>
                            )}
                            {it.highlight && !badge && (
                              <span className="text-[9px] uppercase tracking-widest text-gold">Novo</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </div>
  );
}

// -----------------------------------------------------------------------------
// P0.4B — Sheet de conta (mobile). Nome, email, role, logout.
// -----------------------------------------------------------------------------
function AdminAccountSheet({ open, onClose, user, role, logout }) {
  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-[60]" data-testid="admin-account-sheet" role="dialog" aria-modal="true">
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar conta"
        className="absolute inset-0 bg-navy/60"
      />
      <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-xl p-5 pt-3 pb-8 space-y-4">
        <div className="mx-auto w-10 h-1 rounded-full bg-navy/15" />
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Conta</p>
          <p className="font-display text-2xl text-navy mt-1">{user?.name || "Administrador"}</p>
          <p className="text-sm text-navy/60 mt-0.5 break-all">{user?.email}</p>
          <span className="inline-flex items-center px-2.5 py-0.5 mt-2 rounded-full bg-gold/15 text-[10px] uppercase tracking-widest text-navy">
            {role || "user"}
          </span>
        </div>
        <div className="flex flex-col gap-2 pt-2 border-t border-border">
          <Link
            to="/"
            onClick={onClose}
            data-testid="admin-account-back-to-app"
            className="inline-flex items-center justify-center rounded-full w-full border border-border py-2.5 text-sm text-navy hover:bg-[hsl(var(--parchment))]"
          >
            ← Voltar ao aplicativo
          </Link>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="rounded-full w-full"
            data-testid="admin-account-close"
          >
            Fechar
          </Button>
          <Button
            type="button"
            onClick={logout}
            data-testid="admin-account-logout"
            className="rounded-full w-full bg-navy text-white hover:bg-gold hover:text-navy"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </div>
    </div>
  );
}
